import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useLanguage } from '../../../hooks/useLanguage';
import { myAppRoutes } from '../routes';
import { aside as frAside } from '../../../locales/fr/aside';
import { aside as arAside } from '../../../locales/ar/aside';
import { aside as esAside } from '../../../locales/es/aside';
import useMyAppStore from '../../../stores/myAppStore';
import { getAllowedRoutes } from '../../../services/AppDataServices/config/activity-routes.config';

const translations = {
  fr: frAside,
  ar: arAside,
  es: esAside
};

const Aside = ({ isOpen, onClose }) => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const [openGroups, setOpenGroups] = useState([t.groups.general]);
  const { currentActivity } = useMyAppStore();
  const [allowedRoutes, setAllowedRoutes] = useState([]);

  // Mettre à jour les routes autorisées quand l'activité change
  useEffect(() => {
    if (currentActivity) {
      const routes = getAllowedRoutes(currentActivity.id);
      // console.log('Routes autorisées:', routes);
      setAllowedRoutes(routes);
    } else {
      setAllowedRoutes([]);
    }
  }, [currentActivity]);

  // Mettre à jour les groupes ouverts quand la langue change
  useEffect(() => {
    setOpenGroups(prev => 
      prev.map(group => {
        const key = Object.entries(t.groups).find(([_, value]) => value === group)?.[0];
        return key ? t.groups[key] : group;
      })
    );
  }, [lang, t.groups]);

  const routeLabels = {
    '': t.routes.home,
    'dashboard': t.routes.dashboard,
    'activites': t.routes.activities,
    'users': t.routes.users,
    'profile': t.routes.profile,
    'test-store': t.routes.testStore,
    'products': t.routes.products,
    'categories': t.routes.categories,
    'customers': t.routes.customers,
    'sales': t.routes.sales,
    'approvisionnements': t.routes.stockMovements,
    'settings': t.routes.settings,
    'modeles-vente': t.routes.saleModels,
    'tables': t.routes.tables,
    'appointments': t.routes.appointments,
    'commandes': t.routes.commandes,
    'activity-settings': t.routes.activitySettings
  };

  const routeGroups = {
    [t.groups.general]: ['dashboard', 'activites'],
    [t.groups.management]: ['products', 'categories', 'customers', 'sales', 'approvisionnements'],
    [t.groups.planning]: ['tables', 'appointments', 'commandes'],
    [t.groups.configuration]: ['settings', 'modeles-vente', 'activity-settings'],
    [t.groups.account]: ['users', 'profile', 'test-store']
  };

  const toggleGroup = (group) => {
    setOpenGroups(prev => 
      prev.includes(group) 
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  // Fonction pour vérifier si une route est accessible
  const isRouteAccessible = (routePath) => {
    // Les routes générales sont toujours accessibles
    if (routePath === 'activites' || routePath === 'dashboard') {
      return true;
    }
    // Si aucune activité n'est sélectionnée, seules les routes générales sont accessibles
    if (!currentActivity) {
      return false;
    }
    // Vérifier si la route est autorisée pour l'activité actuelle
    const isAllowed = allowedRoutes.includes(routePath);
    // console.log(`Route ${routePath} accessible:`, isAllowed);
    return isAllowed;
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[100] print-hide"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-72 bg-gradient-to-b from-gray-900 to-gray-800 text-white transform transition-transform duration-300 ease-in-out z-[110] print-hide ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex justify-end p-4 border-b border-gray-700">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white focus:outline-none transition-colors duration-200"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="p-4">
          {/* Lien vers la racine */}
          <div className="mb-6">
            <NavLink
              to={`/${lang}/`}
              className={({ isActive }) =>
                `flex items-center px-4 py-3 rounded-lg transition-colors duration-200 ${
                  isActive 
                    ? 'bg-indigo-600 text-white' 
                    : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
                }`
              }
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              {t.routes.home}
            </NavLink>
          </div>

          {Object.entries(routeGroups).map(([group, routes]) => {
            // Filtrer les routes accessibles pour ce groupe
            const accessibleRoutes = routes.filter(isRouteAccessible);
            
            // Ne pas afficher le groupe s'il n'y a pas de routes accessibles
            if (accessibleRoutes.length === 0) return null;

            return (
              <div key={group} className="mb-4">
                <button
                  onClick={() => toggleGroup(group)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-200 uppercase tracking-wider hover:text-white focus:outline-none transition-colors duration-200 rounded-lg hover:bg-gray-700/50"
                >
                  <span>{group}</span>
                  <svg 
                    className={`w-4 h-4 transform transition-transform duration-200 ${
                      openGroups.includes(group) ? 'rotate-180' : ''
                    }`}
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                <div className={`overflow-hidden transition-all duration-200 ${
                  openGroups.includes(group) ? 'max-h-96' : 'max-h-0'
                }`}>
                  <ul className="space-y-1 py-2">
                    {accessibleRoutes.map((routePath) => {
                      const route = myAppRoutes.find(r => r.path === routePath);
                      if (!route) return null;
                      
                      return (
                        <li key={routePath}>
                          <NavLink
                            to={`/${lang}/myapp/${route.path}`}
                            className={({ isActive }) =>
                              `block px-4 py-2.5 rounded-lg transition-colors duration-200 ${
                                isActive 
                                  ? 'bg-indigo-600 text-white' 
                                  : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
                              }`
                            }
                          >
                            {routeLabels[routePath]}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

export default Aside; 